import { useEffect, useRef, useState } from "react";
import { Volume2, Pause, Play, Square } from "lucide-react";
import { useI18n } from "@/lib/i18n";

function stripHtml(html: string): string {
  const div = document.createElement("div");
  div.innerHTML = html;
  return (div.textContent || div.innerText || "").replace(/\s+/g, " ").trim();
}

/**
 * Lecture audio automatique de l'article via la synthèse vocale du
 * navigateur (Web Speech API) — aucun fichier audio à générer/héberger.
 * Le navigateur bloquant le démarrage automatique du son sans interaction,
 * la lecture démarre au clic sur "Écouter l'article", puis se déroule
 * automatiquement de bout en bout (titre, chapô, corps).
 */
export function ReadAloud({ title, summary, corpsHtml }: { title: string; summary?: string; corpsHtml?: string }) {
  const { locale, t } = useI18n();
  const [supported, setSupported] = useState(false);
  const [status, setStatus] = useState<"idle" | "playing" | "paused">("idle");
  const utteranceRef = useRef<SpeechSynthesisUtterance | null>(null);

  useEffect(() => {
    setSupported(typeof window !== "undefined" && "speechSynthesis" in window);
    return () => {
      if (typeof window !== "undefined" && "speechSynthesis" in window) {
        window.speechSynthesis.cancel();
      }
    };
  }, []);

  const texte = [title, summary, corpsHtml ? stripHtml(corpsHtml) : ""]
    .filter(Boolean)
    .join(". ");

  const demarrer = () => {
    if (!supported || !texte) return;
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(texte);
    utterance.lang = locale === "fr" ? "fr-FR" : "en-US";
    utterance.onend = () => setStatus("idle");
    utterance.onerror = () => setStatus("idle");
    utteranceRef.current = utterance;
    window.speechSynthesis.speak(utterance);
    setStatus("playing");
  };

  const basculerPause = () => {
    if (!supported) return;
    if (status === "playing") {
      window.speechSynthesis.pause();
      setStatus("paused");
    } else if (status === "paused") {
      window.speechSynthesis.resume();
      setStatus("playing");
    }
  };

  const arreter = () => {
    if (!supported) return;
    window.speechSynthesis.cancel();
    setStatus("idle");
  };

  if (!supported) return null;

  return (
    <div className="inline-flex items-center gap-2 rounded-md border border-border bg-surface px-3 py-1.5 text-xs font-semibold">
      {status === "idle" ? (
        <button
          onClick={demarrer}
          className="inline-flex items-center gap-1.5 text-foreground hover:text-verified transition-colors"
        >
          <Volume2 className="h-3.5 w-3.5" />
          {t("article.listen")}
        </button>
      ) : (
        <>
          <button
            onClick={basculerPause}
            aria-label={status === "playing" ? "Pause" : "Play"}
            className="inline-flex items-center gap-1.5 text-foreground hover:text-verified transition-colors"
          >
            {status === "playing" ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
            {t("article.listening")}
          </button>
          <button
            onClick={arreter}
            aria-label="Stop"
            className="text-muted-foreground hover:text-alert transition-colors"
          >
            <Square className="h-3 w-3" />
          </button>
        </>
      )}
    </div>
  );
}
